import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import callrecorderApi from '../../api/callrecorderApi';
import callrecorderUrls from "../../configs/callrecorder/callrecorderUrls";
import { UserNumberMessageInterface } from '../../store/callrecorder/userNumberMessagesSlice';

const PREFIX = 'callrecorder';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: callrecorderUrls.userNumberMessagePath(value.id)
  };
}

export const userNumberMessagesEntity = new schema.Entity(
  'userNumberMessages',
  {},
  {
    processStrategy
  }
);

export const userNumberMessageEntity = new schema.Entity(
  'userNumberMessage',
  {},
  {
    processStrategy
  }
);

const fetchUserNumberMessagesCollection = async (url: string, params: object = {}) => {
  const response = await callrecorderApi.get(url, { params })
  const normalized = normalize<
    any,
    {
      userNumberMessages: { [key: string]: UserNumberMessageInterface };
    }
  >(response.data.user_number_messages, [userNumberMessagesEntity]);

  return {
    userNumberMessages: normalized.entities?.userNumberMessages || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    { userNumberMessage: UserNumberMessageInterface; }
  >(response.data.user_number_message, userNumberMessageEntity);
  return normalized.entities;
}

export interface ConversationItem {
  id: number;
  user_number_id: number;
  contact_number: string;
  last_message_body: string;
  last_message_at: string;
  last_message_direction: 'inbound' | 'outbound';
  unread_count: number;
  created_at: string;
  updated_at: string;
}

export const fetchConversations = createAsyncThunk(
  `${PREFIX}_userNumberMessages/fetchConversations`,
  async (userNumberId: string | number) => {
    const response = await callrecorderApi.get(`/onesky/user_numbers/${userNumberId}/conversations`);
    return response.data.conversations as ConversationItem[];
  }
);

export const fetchConversationMessages = createAsyncThunk(
  `${PREFIX}_userNumberMessages/fetchConversationMessages`,
  async ({ conversationId, params }: { conversationId: number; params?: object }) =>
    fetchUserNumberMessagesCollection(`/onesky/conversations/${conversationId}/messages`, params)
);

export const fetchUserNumberMessages = createAsyncThunk(
  `${PREFIX}_userNumberMessages/fetchUserNumberMessages`,
  async (params: object = {}) => fetchUserNumberMessagesCollection('/onesky/user_number_messages', params)
);

export const fetchUserNumberMessageNumbers = createAsyncThunk(
  `${PREFIX}_userNumberMessages/fetchUserNumberMessageNumbers`,
  async (params: object = {}) => {
    const response = await callrecorderApi.get('/onesky/user_number_messages/numbers', { params });
    return response.data.numbers as string[];
  }
);

export const fetchUserNumberMessage = createAsyncThunk(
  `${PREFIX}_userNumberMessages/fetchUserNumberMessage`,
  async (id: number | string) => {
    const response = await callrecorderApi.get(`/onesky/user_number_messages/${id}`);
    return normalizeInstance(response);
  }
);
